import { useEffect, useState } from 'react';
import { Loader2, Copy as CopyIcon, Send, Trash2, Plus, Link2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

/**
 * Share dialog — three ways to hand a catalog to someone else:
 *   • Members: add a buyer's email so it shows up in their signed-in dashboard
 *     and optionally email them a notification.
 *   • Invite links: mint tokenised URLs anyone can open without an account.
 *   • Copy public link: shortcut to the plain public URL when the catalog is
 *     already flipped to public.
 */
export default function CatalogShareDialog({ catalog, open, onOpenChange, onChanged }) {
  const [members, setMembers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !catalog) return;
    setMembers(catalog.shared_with_emails || []);
    setTokens(catalog.share_tokens || []);
    setNewEmail('');
  }, [open, catalog]);

  if (!catalog) return null;

  const publicUrl = `https://ijehaus.com/c/${catalog.slug}`;

  const addMember = async (e) => {
    e?.preventDefault();
    if (!newEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/catalogs/${catalog.id}/members`, { email: newEmail.trim(), notify });
      setMembers(data.shared_with_emails || []);
      setNewEmail('');
      toast.success(data.email_sent ? 'Added + email sent' : 'Member added');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add failed');
    } finally { setBusy(false); }
  };

  const removeMember = async (email) => {
    if (!window.confirm(`Remove ${email} from this catalog?`)) return;
    try {
      const { data } = await api.delete(`/catalogs/${catalog.id}/members/${encodeURIComponent(email)}`);
      setMembers(data.shared_with_emails || []);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    }
  };

  const mintToken = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/catalogs/${catalog.id}/tokens`);
      setTokens(data.share_tokens || []);
      toast.success('Invite link created');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const revokeToken = async (token) => {
    if (!window.confirm('Revoke this invite link? Anyone using it will lose access.')) return;
    try {
      const { data } = await api.delete(`/catalogs/${catalog.id}/tokens/${encodeURIComponent(token)}`);
      setTokens(data.share_tokens || []);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch { toast.error('Copy failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="catalog-share-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Share catalog</DialogTitle>
          <p className="sans text-xs tracking-widest uppercase text-neutral-500">{catalog.name}</p>
        </DialogHeader>

        <Tabs defaultValue="members" className="mt-2">
          <TabsList className="grid grid-cols-2 w-full bg-neutral-100 rounded-none h-10 p-0">
            <TabsTrigger value="members" className="rounded-none sans text-xs tracking-widest uppercase data-[state=active]:bg-white" data-testid="share-tab-members">Members</TabsTrigger>
            <TabsTrigger value="links" className="rounded-none sans text-xs tracking-widest uppercase data-[state=active]:bg-white" data-testid="share-tab-links">Invite Links</TabsTrigger>
          </TabsList>

          {/* Members tab */}
          <TabsContent value="members" className="py-4 space-y-4">
            <p className="sans text-xs text-neutral-500 leading-relaxed">
              Give a buyer account access — this catalog will show up in their
              dashboard next time they sign in. Toggle the notify option to email
              them a branded invite from IJE HAUS Wholesale.
            </p>
            <form onSubmit={addMember} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="buyer@example.com"
                className="ije-input h-10 flex-1"
                data-testid="share-new-email"
              />
              <button type="submit" disabled={busy || !newEmail.trim()} className="btn-primary h-10" data-testid="share-add-member">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Add
              </button>
            </form>
            <label className="flex items-center gap-2 sans text-xs text-neutral-600">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                data-testid="share-notify"
              />
              Also email them a link
            </label>

            {members.length === 0 ? (
              <div className="border border-dashed border-neutral-200 p-6 text-center sans text-sm text-neutral-500">
                No one has been invited yet.
              </div>
            ) : (
              <ul className="border border-neutral-200 divide-y divide-neutral-100" data-testid="share-members-list">
                {members.map((m) => (
                  <li key={m} className="flex items-center justify-between px-3 py-2">
                    <span className="sans text-sm text-neutral-900 truncate">{m}</span>
                    <button
                      type="button"
                      onClick={() => removeMember(m)}
                      className="p-1.5 text-neutral-400 hover:text-red-600"
                      title="Remove"
                      data-testid={`share-remove-${m}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          {/* Invite links tab */}
          <TabsContent value="links" className="py-4 space-y-4">
            <p className="sans text-xs text-neutral-500 leading-relaxed">
              Mint a tokenised link anyone can open without signing in — good for
              text messages, DMs, or QR-adjacent handoffs. Revoke any time.
            </p>
            <button
              type="button"
              onClick={mintToken}
              disabled={busy}
              className="btn-primary h-10"
              data-testid="share-mint-token"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} New invite link
            </button>

            {tokens.length === 0 ? (
              <div className="border border-dashed border-neutral-200 p-6 text-center sans text-sm text-neutral-500">
                No invite links yet.
              </div>
            ) : (
              <ul className="border border-neutral-200 divide-y divide-neutral-100" data-testid="share-tokens-list">
                {tokens.map((t) => {
                  const url = `https://ijehaus.com/c/${catalog.slug}?token=${t}`;
                  return (
                    <li key={t} className="px-3 py-2 flex items-center gap-2">
                      <p className="sans text-xs font-mono text-neutral-700 flex-1 truncate" title={url}>{url}</p>
                      <button
                        type="button"
                        onClick={() => copy(url)}
                        className="p-1.5 text-neutral-400 hover:text-black"
                        title="Copy link"
                        data-testid={`share-copy-token-${t}`}
                      >
                        <CopyIcon size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => revokeToken(t)}
                        className="p-1.5 text-neutral-400 hover:text-red-600"
                        title="Revoke"
                        data-testid={`share-revoke-token-${t}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {catalog.visibility === 'public' && (
              <div className="mt-4 border-t border-neutral-200 pt-4">
                <p className="sans text-[10px] tracking-widest uppercase text-neutral-500 mb-2">Public link</p>
                <div className="flex items-center gap-2">
                  <p className="sans text-xs font-mono text-neutral-700 flex-1 truncate" title={publicUrl}>{publicUrl}</p>
                  <button
                    type="button"
                    onClick={() => copy(publicUrl)}
                    className="p-1.5 text-neutral-400 hover:text-black"
                    title="Copy public link"
                    data-testid="share-copy-public"
                  >
                    <Link2 size={14} />
                  </button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
